import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 画面表示のタイムゾーン。
 *
 * 利用者・営業先・業務 SLA のすべてが日本時間で動くため、閲覧端末の設定に
 * かかわらず JST で表示する。ブラウザ TZ に任せると、UTC 設定の端末（VDI・
 * リモートデスクトップ・海外出張中の端末）で日付が 1 日ずれ、「今日の反応」が
 * 「昨日」と出て架電の優先度を誤らせる。
 */
export const DISPLAY_TIMEZONE = "Asia/Tokyo";

/**
 * タイムゾーンを持たない日付のみの文字列（例 `2026-09-11` / `2026/09/11`）。
 *
 * これは「瞬間」ではなく**暦日**なので、タイムゾーン変換してはいけない。
 * 素の `dayjs("2026-09-11")` は閲覧端末の 0 時として読むため、JST に直すと
 * UTC+10 以東の端末（シドニー・オークランド等）では前日の `2026/09/10` になる。
 *
 * バックエンドの `date` 型はハイフン区切りで届く。スラッシュ区切りは画面表示の
 * 書式だが、念のため同じく暦日として扱う。区切り文字は後方参照で揃っている
 * ものに限る（`2026-09/11` のような混在は日付として扱わない）。
 */
const DATE_ONLY_PATTERN = /^\d{4}([-/])\d{2}\1\d{2}$/;

/**
 * 日時を JST の dayjs に変換する（引数なしなら現在時刻）。
 *
 * **表示に関わる日時はすべてこの関数を通すこと。** 素の `dayjs()` は
 * 閲覧端末のタイムゾーンで書式化・日境界判定を行うため、ここを迂回すると
 * 端末ごとに違う日時が出る。
 *
 * 日付のみの文字列（`DATE_ONLY_PATTERN`）は JST の暦日として読む。
 * @param {Date | string} [date] 対象の日時。省略時は現在時刻
 * @return {dayjs.Dayjs} JST の dayjs
 */
function jst(date?: Date | string): dayjs.Dayjs {
  if (typeof date === "string" && DATE_ONLY_PATTERN.test(date)) {
    return dayjs.tz(date, DISPLAY_TIMEZONE);
  }
  return dayjs(date).tz(DISPLAY_TIMEZONE);
}

/**
 * 相対時間を計算して日本語で返す関数
 * @param {Date | string} date 対象の日時
 * @return {string} 相対時間の文字列（例：「3時間前」）
 */
export function timeAgo(date: Date | string): string {
  // 差分は絶対時刻どうしの引き算なのでタイムゾーンに依存しない（ここだけ jst() 不要）
  const now = dayjs();
  const targetDate = dayjs(date);
  const diff = now.diff(targetDate, "second"); // 秒差

  if (diff < 60) return `${diff}秒前`;
  if (diff < 3600) return `${Math.floor(diff / 60)}分前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}時間前`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}日前`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)}週間前`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)}カ月前`;
  return `${Math.floor(diff / 31536000)}年前`;
}

/**
 * 反応のあった日を「今日 / 昨日 / 一昨日 / N 日前」で返す。
 *
 * 経過ミリ秒を 24 時間で割るのではなく**暦日の差**で数える。営業が見るのは
 * 「今日の反応か」であって「24 時間以内か」ではないため、昨夜 23 時の反応は
 * 経過 10 時間でも「昨日」と出したい。
 * @param {Date | string} date 対象の日時
 * @return {string} 表示ラベル（例：「昨日」「5 日前」）
 */
export function formatRelativeDay(date: Date | string): string {
  const days = calendarDaysAgo(date);
  if (days <= 0) return "今日";
  if (days === 1) return "昨日";
  if (days === 2) return "一昨日";
  return `${days} 日前`;
}

/**
 * 直近 3 日だけ語で、それより前は月日で日時を返す（例：「昨日 23:41」「08/12 09:22」）。
 *
 * 時刻を落とさないのは、宛先一覧が「最終反応の新しい順」で並ぶため。1 配信の
 * 反応は送信から数日に集中するので、日付だけにすると大半の行が「今日」に潰れて
 * 並び順の理由が画面から読めなくなる。時刻そのものも、いつ架電するかの判断材料。
 * @param {Date | string | null | undefined} date 対象の日時
 * @param {string} [fallback] 日時が無いときの表示文字（既定は `—`）
 * @return {string} 表示ラベル
 */
export function formatRelativeDayTime(
  date: Date | string | null | undefined,
  fallback = "—",
): string {
  if (!date) return fallback;
  const time = jst(date).format("HH:mm");
  const days = calendarDaysAgo(date);
  if (days <= 0) return `今日 ${time}`;
  if (days === 1) return `昨日 ${time}`;
  if (days === 2) return `一昨日 ${time}`;
  return formatShortDateTime(date);
}

/**
 * その日時が JST の「今日」か（未来も今日として扱う）を返す。
 *
 * New バッジのように「今日届いたか」を色分けする判定用。素の
 * `dayjs().startOf("day")` はブラウザ TZ の日境界で切れるため、UTC 端末では
 * JST の午前 9 時前に届いたメールが「今日」から外れる。
 * @param {Date | string} date 対象の日時
 * @return {boolean} JST の今日（または未来）なら true
 */
export function isToday(date: Date | string): boolean {
  return calendarDaysAgo(date) <= 0;
}

/**
 * 今日から見て何暦日前かを JST の暦日境界で返す（未来は負数）。
 *
 * 「何日前までを相対表示にするか」のしきい値は画面ごとに違うため、判定の材料
 * だけを公開する。呼び出し側で日境界を計算し直すと端末 TZ に引きずられる。
 * @param {Date | string} date 対象の日時
 * @return {number} 暦日の差
 */
export function calendarDaysAgo(date: Date | string): number {
  return jst().startOf("day").diff(jst(date).startOf("day"), "day");
}

/**
 * 絶対日時をフォーマットして返す関数
 * @param {Date | string} date 対象の日時
 * @return {string} フォーマットされた絶対日時（例：「2025/01/15 14:30:25」）
 */
export function formatAbsoluteTime(date: Date | string): string {
  return jst(date).format("YYYY/MM/DD HH:mm:ss");
}

/**
 * 指定した書式で日時を返す関数を作る。
 *
 * 書式だけが違うフォーマッタを個別に書くと、`fallback` の既定値や
 * 「日時が無いときは書式に通さない」という約束が本数ぶん複製される。
 * ここ 1 か所に閉じておけば、増やすときも書式文字列を選ぶだけで済む。
 * @param {string} pattern dayjs の書式文字列
 * @return {Function} 日時フォーマッタ（日時と fallback を受け取る）
 */
const formatWith =
  (pattern: string) =>
  (date: Date | string | null | undefined, fallback = "—"): string =>
    date ? jst(date).format(pattern) : fallback;

/**
 * 分までの日時表記 (YYYY/MM/DD HH:mm) を返す関数。
 * @param {Date | string | null | undefined} date 対象の日時
 * @param {string} [fallback] 日時が無いときの表示文字（既定は `—`）
 * @return {string} フォーマットされた日時
 */
export const formatDateTime = formatWith("YYYY/MM/DD HH:mm");

/**
 * 月日だけの表記 (MM/DD) を返す関数。グラフの横軸ラベル等の狭い場所向け。
 * @param {Date | string | null | undefined} date 対象の日時
 * @param {string} [fallback] 日時が無いときの表示文字（既定は `—`）
 * @return {string} フォーマットされた月日
 */
export const formatMonthDay = formatWith("MM/DD");

/**
 * 短縮日時表記 (MM/DD HH:mm) を返す関数。タイムライン等の狭いカラム向け。
 * @param {Date | string | null | undefined} date 対象の日時
 * @param {string} [fallback] 日時が無いときの表示文字（既定は `—`）
 * @return {string} 短縮日時表記
 */
export const formatShortDateTime = formatWith("MM/DD HH:mm");

/**
 * 日付だけの表記 (YYYY/MM/DD) を返す関数。一覧の「作成日」「更新日」向け。
 * @param {Date | string | null | undefined} date 対象の日時
 * @param {string} [fallback] 日時が無いときの表示文字（既定は `—`）
 * @return {string} フォーマットされた日付
 */
export const formatDate = formatWith("YYYY/MM/DD");

/**
 * 和文の日付表記 (YYYY年M月D日) を返す関数。招待の有効期限など、文章に混ぜる場所向け。
 * @param {Date | string | null | undefined} date 対象の日時
 * @param {string} [fallback] 日時が無いときの表示文字（既定は `—`）
 * @return {string} フォーマットされた日付
 */
export const formatLongDate = formatWith("YYYY年M月D日");

/**
 * 相対時間と絶対時間の両方を管理する型
 */
export type TimeDisplay = {
  relative: string;
  absolute: string;
};

/**
 * 相対時間と絶対時間の両方を計算して返す関数
 * @param {Date | string} date 対象の日時
 * @return {TimeDisplay} 相対時間と絶対時間のオブジェクト
 */
export function getTimeDisplay(date: Date | string): TimeDisplay {
  return {
    relative: timeAgo(date),
    absolute: formatAbsoluteTime(date),
  };
}

/**
 * JST の日時を `<input type="datetime-local">` の値に変換する。
 *
 * `datetime-local` はタイムゾーンを持たない裸の壁時計文字列しか扱えないため、
 * 「どこの壁時計か」を決めるのは呼び出し側の責任になる。ここでは JST に固定する。
 *
 * **`fromDateTimeLocalValue` と対で使うこと。** 一方だけ差し替えると、入力欄の
 * 表示と保存値がずれ、編集していない予約日時が保存し直すだけで動く。
 * @param {Date | string | null | undefined} date 対象の日時（サーバ由来のオフセット付き文字列）
 * @return {string} `YYYY-MM-DDTHH:mm`。日時が無ければ空文字
 */
export function toDateTimeLocalValue(
  date: Date | string | null | undefined,
): string {
  return date ? jst(date).format("YYYY-MM-DDTHH:mm") : "";
}

/**
 * `<input type="datetime-local">` の値を JST の壁時計として読み、ISO 文字列で返す。
 *
 * 素の `new Date("2026-09-08T10:00")` は閲覧端末のタイムゾーンとして解釈されるため、
 * UTC 端末では 9 時間ずれた時刻が保存される。JST を明示して読むことでそれを防ぐ。
 * @param {string | null | undefined} value `YYYY-MM-DDTHH:mm` 形式の入力値
 * @return {string | null} オフセット付き ISO 文字列。値が無ければ null
 */
export function fromDateTimeLocalValue(
  value: string | null | undefined,
): string | null {
  return value ? dayjs.tz(value, DISPLAY_TIMEZONE).toISOString() : null;
}

/**
 * 現在の JST 日時を `<input type="datetime-local">` の値として返す。
 * 入力欄の `min` / 既定値に使う。
 * @return {string} `YYYY-MM-DDTHH:mm`
 */
export function nowDateTimeLocalValue(): string {
  return jst().format("YYYY-MM-DDTHH:mm");
}

/**
 * 現在の JST 日付を `<input type="date">` の値として返す。
 *
 * `new Date().toISOString().slice(0, 10)` は UTC 日付なので、JST の朝 9 時前に
 * 開くと前日が入る。その取り違えを避けるための関数。
 * @return {string} `YYYY-MM-DD`
 */
export function nowDateValue(): string {
  return jst().format("YYYY-MM-DD");
}
